import type { ReactElement } from 'react'

import { EmptyState, ErrorState, Spinner, Tag } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import styles from './OperationsPage.module.scss'

/**
 * The plant's home, and a DELIBERATE placeholder: which screens the operator
 * works on — the MO list, picking a machine inside the group, starting and
 * stopping — is another plan's decision, and guessing them here would ship a
 * flow nobody agreed to.
 *
 * What it does do is prove the wiring end to end with the two reads that will
 * feed every one of those screens: the caller's own groups
 * (`assignments.myGroups`), and what is running on the first of them
 * (`operations.listRunning`) with its freshness. If this screen is right, the
 * cookie session, the refresh, superjson's dates and the group scope of the
 * mirror all work.
 */
const OPERATIONS_PENDING = 'Pantallas de operaciones pendientes de definir.'

/** The count, in Spanish rather than in `N operaciones` with N = 1. */
const runningCountText = (count: number): string => {
  if (count === 0) {
    return 'Sin operaciones en curso'
  }
  return count === 1 ? '1 operación en curso' : `${count} operaciones en curso`
}

type RunningSummaryProps = {
  stationGroupId: string
}

/**
 * How much is running on one group, and how old that answer is.
 *
 * Its own component so the query is never called with a placeholder id: the page
 * renders it only once there IS a first group, instead of firing
 * `listRunning({ stationGroupId: '' })` behind an `enabled` flag.
 *
 * A count and nothing else on purpose — the rows carry the MO, the dish and the
 * machine, and putting them on screen is designing the operations flow.
 */
const RunningSummary = ({ stationGroupId }: RunningSummaryProps): ReactElement => {
  // Polled, not read once: MARS keeps syncing while nobody touches the tablet,
  // and a count that never renews ages behind its own freshness badge.
  const running = trpc.operations.listRunning.useQuery(
    { stationGroupId },
    { refetchInterval: MIRROR_REFETCH_MS },
  )

  if (running.isPending) {
    return <Spinner label="Contando operaciones…" />
  }
  if (running.isError) {
    return <ErrorState error={running.error} onRetry={() => void running.refetch()} />
  }

  return (
    <div className={styles['operations-page__running']}>
      <p className={styles['operations-page__running-count']}>
        {runningCountText(running.data.data.length)}
      </p>
      {/* The mirror runs up to a couple of minutes behind: the lag is shown next
          to the number it belongs to, never hidden. */}
      <FreshnessBadge freshness={running.data.freshness} />
    </div>
  )
}

/**
 * The caller's active groups as cards. Loading, failed and empty are three
 * visibly different answers — an empty list on a plant screen reads as "there is
 * no work", which is exactly what a silently failed request must not say.
 */
const MyGroupsPanel = (): ReactElement => {
  const myGroups = trpc.assignments.myGroups.useQuery()

  if (myGroups.isPending) {
    return <Spinner label="Cargando tus grupos…" />
  }
  if (myGroups.isError) {
    return <ErrorState error={myGroups.error} onRetry={() => void myGroups.refetch()} />
  }
  if (myGroups.data.length === 0) {
    return (
      <EmptyState
        title="No estás asignado a ningún grupo"
        message="Pide a tu responsable que te asigne a un grupo de estaciones."
      />
    )
  }

  return (
    <ul className={styles['operations-page__groups']}>
      {myGroups.data.map((group, index) => (
        <li key={group.stationGroupId} className={styles['operations-page__group']}>
          <div className={styles['operations-page__group-head']}>
            <h2 className={styles['operations-page__group-name']}>{group.name}</h2>
            {/* A retired group keeps its active assignments, and the operator can
                still act on it — so it is MARKED here and never dropped. */}
            {!group.active && <Tag label="Grupo retirado" tone="warn" />}
          </div>
          {index === 0 && <RunningSummary stationGroupId={group.stationGroupId} />}
        </li>
      ))}
    </ul>
  )
}

export const OperationsPage = (): ReactElement => (
  <section className={styles['operations-page']}>
    <h1 className={styles['operations-page__title']}>Operaciones</h1>
    <p className={styles['operations-page__notice']}>{OPERATIONS_PENDING}</p>
    <MyGroupsPanel />
  </section>
)
